'use client'
import { fetchAutocompleteResults } from '@/app/api/search'
import { fetchLocationElasticSearchClient } from '@/app/api/search-client'
import { PREV_LANG_LOCAL_STORAGE_KEY } from '@/app/constants'
import {
	DestPagesProps,
	LocationType,
	SearchItem,
	SearchItemLocationType,
} from '@/app/types'
import {
	getLocationTypeByQuery,
	isHood,
	isLandmark,
	parseSearchParamsToLower,
} from '@/app/utils'
import { useLocalStorage } from '@uidotdev/usehooks'
import { camelCase, startCase } from 'lodash'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { FC, useEffect, useRef } from 'react'

export interface DisplayData {
	displayValue: string
	displayCountry: string
	displayCity: string | undefined
	displayRegion: string | undefined
	displayHotel: string | undefined
	displayName: string
}
interface LocationDataHandlerProps extends DestPagesProps {
	mainPage?: boolean
	hotelPage?: boolean
	setDisplayValue: (data: DisplayData) => DisplayData
	setTargetLocation: (location: SearchItem) => SearchItem
}

const LocationDataHandler: FC<LocationDataHandlerProps> = (props) => {
	const { mainPage, setTargetLocation, setDisplayValue } = props
	const t = useTranslations('Keywords')
	const [prevLang, _] = useLocalStorage(PREV_LANG_LOCAL_STORAGE_KEY, 'en')
	const lang = props.params?.lang
	const router = useRouter()

	const searchParams = parseSearchParamsToLower(props?.searchParams)
	const destCode = searchParams?.destcode
	const fetchedRef = useRef(false)

	useEffect(() => {
		const fetchData = async () => {
			if (fetchedRef.current) return
			fetchedRef.current = true

			const targetLocation = await getTargetLocation()
			if (targetLocation) {
				setTargetLocation(targetLocation as SearchItem)
			}

			const displayValue = await getDisplayValue()
			if (displayValue) {
				setDisplayValue(displayValue)
			}
		}

		fetchData()
	}, [mainPage, destCode, lang])

	const getTargetLocation = async () => {
		let [country, region, city, hotel, place] = props.params?.dest || []
		let locationTypeLong = ''
		let loctype = searchParams?.loctype
		let airportname = ''
		let placeID = searchParams?.['placeid'] || null
		const hotelID = searchParams?.['hotelid'] || null
		const countryName = country && decodeURIComponent(country)
		const cityName = decodeURIComponent(
			searchParams?.['locationname'] as string
		)

		if (mainPage && destCode) {
			const query = destCode
			locationTypeLong = getLocationTypeByQuery(query as string) as string

			try {
				const searchResult = await fetchLocationElasticSearchClient(
					query as string,
					lang
				)

				if (
					locationTypeLong === LocationType.CITY &&
					!!searchResult.results?.length
				) {
					const {
						country: countryRaw,
						state: stateRaw,
						name: nameRaw,
					} = searchResult.results[0]
					country = countryRaw.raw
					region = stateRaw.raw
					city = nameRaw.raw
					loctype = SearchItemLocationType.CITY
				} else if (locationTypeLong === LocationType.AIRPORT && searchResult) {
					const {
						country: countryResult,
						state: stateResult,
						name: nameResult,
						city: cityResult,
					} = searchResult

					country = countryResult
					region = stateResult || cityResult
					city = cityResult
					airportname = nameResult
					loctype = SearchItemLocationType.AIRPORT
				}
			} catch (error) {
				console.log(
					'error getTargetLocation, Faulty Destcode or empty response:',
					error
				)
				return null
			}
		}

		let searchQuery = ''

		if (hotel) {
			if (isLandmark(hotel)) {
				searchQuery = `${decodeURIComponent(hotel)} ${
					city && city
				} ${countryName}`
			} else {
				searchQuery = `${region} ${city} ${decodeURIComponent(hotel)}`
			}
		} else if (city) {
			if (city === SearchItemLocationType.FREEREGION) {
				if (region !== countryName) {
					searchQuery = `${region} ${countryName || country}`
				} else {
					searchQuery = region || countryName || country
				}
			} else {
				const regionPart = region ? `${region},` : ''
				const countryPart = countryName || country
				searchQuery = `${city},${regionPart}${countryPart}`
			}
		} else {
			searchQuery = `${region || ''} ${countryName || country}`
		}

		if (city) {
			if (city === SearchItemLocationType.FREEREGION) {
				loctype = SearchItemLocationType.REGION
			} else if (
				city === SearchItemLocationType.AIRPORT ||
				(destCode && getLocationTypeByQuery(destCode) === LocationType.AIRPORT)
			) {
				loctype = SearchItemLocationType.AIRPORT
			} else {
				loctype = SearchItemLocationType.CITY
			}
		} else if (region) {
			loctype = SearchItemLocationType.REGION
		} else if (country) {
			loctype = SearchItemLocationType.COUNTRY
		}

		if (hotel && city !== SearchItemLocationType.AIRPORT) {
			loctype = SearchItemLocationType.HOTEL
		}

		if (props.searchParams.loctype) {
			loctype = props.searchParams.loctype
		}

		const isAirport = city == SearchItemLocationType.AIRPORT

		const ctid = searchParams?.ctid || null
		const countryCode = searchParams?.cc || null
		const fullCityName = `${city || ''} ${region || ''} ${countryName || ''}`
		const timezone = searchParams?.timezone
		const utcOffset = searchParams?.utcoffsetminutes
		const decodedCity = decodeURIComponent(city)
		const decodedRegion = decodeURIComponent(region)
		const decodedPlace = decodeURIComponent(place)
		const decodedSearchQuery = decodeURIComponent(searchQuery)
		const coords =
			searchParams?.latlon && searchParams?.latlon.split(',').map(Number)

		let rawResult = {
			...(searchQuery && { displayValue: decodedSearchQuery }),
			...(searchQuery && { displayname: decodedSearchQuery }),
			...(country && { country: decodeURIComponent(countryName || country) }),
			...(city && { city: decodedCity }),
			...(city && {
				cityonly: isAirport ? cityName : decodedCity,
			}),
			...(region && { region: decodeURIComponent(region) }),
			...(hotel && { hotel: decodeURIComponent(hotel) }),
			...(fullCityName && { cityname: decodeURIComponent(fullCityName) }),
			...(airportname && { airportname: decodeURIComponent(airportname) }),
			...(placeID && { placeID }),
			...(hotelID && { id: hotelID, kayakId: hotelID }),
			...(timezone && { timezone }),
			name: decodeURIComponent(
				hotel || startCase(camelCase(city || region || countryName))
			),
			ctid,
			cc: countryCode,
			kayakType: loctype,
			loctype: loctype,
		} as SearchItem

		if (city === SearchItemLocationType.FREEREGION) {
			rawResult = {
				...rawResult,
				cityonly: decodedRegion,
				city: decodedRegion,
				displayname: decodedRegion,
				displayValue: decodedRegion,
			} as SearchItem
		}

		if (isLandmark(hotel) || isHood(hotel)) {
			rawResult = {
				...rawResult,
				cityonly: decodedCity,
				city: decodedCity,
				region: decodedRegion,
				searchFormPrimary: decodedRegion,
				displayname: decodedPlace,
				displayValue: decodedPlace,
				name: decodedPlace,
				kayakType: SearchItemLocationType.LM,
				loctype: SearchItemLocationType.LM,
				lat: coords?.[0],
				lng: coords?.[1],
				googlePlaceId: searchParams?.['googleplaceid'],
				id: searchParams?.['googleplaceid'],
				...(utcOffset && { utcOffsetMinutes: utcOffset }),
			} as SearchItem
		}

		if (prevLang !== lang && !mainPage) {
			return await fetchAutocompleteResults(
				searchQuery
					.split(' ')
					.filter((item) => item !== 'undefined')
					.join(' '),
				lang
			)
				.then((res) => {
					const data = loctype
						? res.find((item) => item.loctype === loctype) || res[0]
						: res[0]

					const urlParamsObject = new URLSearchParams(window.location.search)
					const {
						cityonly,
						name,
						region,
						searchFormPrimary,
						shortdisplayname,
					} = data

					switch (data.loctype) {
						case SearchItemLocationType.AIRPORT:
							urlParamsObject.set('locationName', cityonly || name)
							break
						case SearchItemLocationType.CITY:
							urlParamsObject.set('locationName', cityonly)
							break
						case SearchItemLocationType.HOTEL:
							urlParamsObject.set('locationName', name)
							break
						case SearchItemLocationType.REGION:
							urlParamsObject.set('locationName', region)
							break
						case SearchItemLocationType.FREEREGION:
							urlParamsObject.set(
								'locationName',
								searchFormPrimary || shortdisplayname
							)
							break
						case SearchItemLocationType.LM:
							urlParamsObject.set('locationName', name)
							break
						case SearchItemLocationType.HOOD:
							urlParamsObject.set('locationName', name)
							break
						default:
							urlParamsObject.set('locationName', name)
					}

					const newUrl = `${
						window.location.pathname
					}?${urlParamsObject.toString()}`
					router.push(newUrl)

					return data
				})
				.catch((e) => {
					console.log('error getTargetLocation:', e)
					return null
				})
		} else {
			return rawResult
		}
	}

	const getDisplayData = async () => {
		const location = await getTargetLocation()

		if (!location) return null

		const regionName =
			location?.loctype === SearchItemLocationType.REGION
				? location?.region
				: location?.loctype === SearchItemLocationType.FREEREGION
				? location?.searchFormPrimary
				: ''

		const {
			country: displayCountry = '',
			cityonly = '',
			// region: displayRegion = regionName,
			name: locationName = '',
			kayakType = '',
			displayname = '',
		} = location || {}

		const rawData = {
			cityonly,
			region: regionName,
			name: locationName,
			kayakType,
			displayname,
		}

		const filteredData = Object.fromEntries(
			Object.entries(rawData).filter(([, value]) => value !== '')
		)

		const data = {
			country: displayCountry,
			city: filteredData.cityonly,
			displayName: displayname,
			region: filteredData.region || '',
			// ...(filteredData.region !== filteredData.cityonly && {
			// }),
			...(kayakType === SearchItemLocationType.AIRPORT && {
				hotel: decodeURIComponent(filteredData.name as string),
			}),
			...(kayakType === SearchItemLocationType.HOTEL && {
				hotel: decodeURIComponent(filteredData.name as string),
			}),
			...(kayakType === SearchItemLocationType.LM && {
				region: location.region || '',
				displayname: filteredData.displayname,
				hotel: SearchItemLocationType.LM,
			}),
		}
		return data
	}

	const getDisplayValue = async () => {
		const displayData = await getDisplayData()

		if (!displayData) return

		const { country, region, city, hotel, displayName } = displayData

		const displayCountry = startCase(country)
		const displayCity = startCase(city || '')
		const displayRegion = startCase(region || '')
		const displayHotel = hotel
		let displayValue = country

		const values = {
			displayValue,
			displayCountry,
			displayCity,
			displayRegion,
			displayHotel,
			displayName,
		}

		if (hotel && city !== SearchItemLocationType.AIRPORT) {
			values.displayHotel = displayHotel
			values.displayValue = `${displayHotel}, ${displayCity}`

			if (isLandmark(hotel)) {
				delete values.displayHotel
				values.displayValue = `${displayName}, ${displayCity}`
			}

			return values as DisplayData
		}

		if (city) {
			if (city === SearchItemLocationType.AIRPORT) {
				values.displayHotel = displayHotel
				values.displayValue = `${displayCountry}, ${displayHotel}`
			} else {
				const location = `${displayCity}, ${
					displayRegion ? displayRegion + ', ' : ''
				}${displayCountry}`
				values.displayValue = location
			}

			return values as DisplayData
		}

		if (region) {
			values.displayValue = ` ${displayRegion}, ${displayCountry}`
		}

		delete values.displayHotel

		return values as DisplayData
	}

	return null
}

export default LocationDataHandler
